/**
 * Applications API Endpoint Management
 *
 * Centralized system for managing applications endpoints across API versions V1, V2, V3.
 * Provides semantic endpoint enumeration and automatic version fallback logic.
 */

/**
 * Semantic enumeration of all applications endpoints.
 * Uses nested const structure with 'as const' for full type safety.
 */
export const ApplicationEndpoints = {
  Version: {
    Get: 'Version.Get',
  },
  Type: {
    Get: 'Type.Get',
  },
  Sidebar: {
    Get: 'Sidebar.Get',
  },
  Status: {
    Get: 'Status.Get',
    RequestForAdditionalInfo: {
      Get: 'Status.RequestForAdditionalInfo.Get',
      Put: 'Status.RequestForAdditionalInfo.Put',
    },
    Resubmit: {
      Get: 'Status.Resubmit.Get',
      Put: 'Status.Resubmit.Put',
    },
    Decline: {
      Get: 'Status.Decline.Get',
      Put: 'Status.Decline.Put',
    },
    Withdraw: {
      Put: 'Status.Withdraw.Put',
    },
    Approve: {
      Put: 'Status.Approve.Put',
    },
  },

  List: {
    ByResearchSite: {
      Get: 'List.ByResearchSite.Get',
    },
    BySponsor: {
      Get: 'List.BySponsor.Get',
    },
    ByPatient: {
      Get: 'List.ByPatient.Get',
    },
    Draft: {
      Search: {
        Get: 'List.Draft.Search.Get',
      },
    },
    FilterData: {
      Get: 'List.FilterData.Get',
    },
    FilterDataForPatient: {
      Get: 'List.FilterDataForPatient.Get',
    },
  },

  Create: {
    Post: 'Create.Post',
  },
  History: {
    Get: 'History.Get',
  },

  StudyIdentification: {
    Get: 'StudyIdentification.Get',
    Put: 'StudyIdentification.Put',
    AutopopulateCountry: {
      Get: 'StudyIdentification.AutopopulateCountry.Get',
    },
  },

  ResearchSite: {
    Get: 'ResearchSite.Get',
    Put: 'ResearchSite.Put',
    Autopopulate: {
      Get: 'ResearchSite.Autopopulate.Get',
    },
  },

  ClinicalLaboratoryFacility: {
    Get: 'ClinicalLaboratoryFacility.Get',
    Put: 'ClinicalLaboratoryFacility.Put',
  },

  ResearchSiteAndClinicalLab: {
    Get: 'ResearchSiteAndClinicalLab.Get',
    Put: 'ResearchSiteAndClinicalLab.Put',
    Autopopulate: {
      Get: 'ResearchSiteAndClinicalLab.Autopopulate.Get',
    },
  },

  EligiblePersonnel: {
    Get: 'EligiblePersonnel.Get',
    Put: 'EligiblePersonnel.Put',
    Autopopulate: {
      Get: 'EligiblePersonnel.Autopopulate.Get',
    },
  },

  ContactPoint: {
    Get: 'ContactPoint.Get',
    Put: 'ContactPoint.Put',
    Autopopulate: {
      Get: 'ContactPoint.Autopopulate.Get',
    },
  },

  AdditionalContactPoint: {
    Get: 'AdditionalContactPoint.Get',
    Put: 'AdditionalContactPoint.Put',
  },

  InstitutionOfficial: {
    Get: 'InstitutionOfficial.Get',
    Put: 'InstitutionOfficial.Put',
    Autopopulate: {
      Get: 'InstitutionOfficial.Autopopulate.Get',
    },
  },

  PatientDemographics: {
    Get: 'PatientDemographics.Get',
    Put: 'PatientDemographics.Put',
    Post: 'PatientDemographics.Post',
  },

  ClinicalSummary: {
    Get: 'ClinicalSummary.Get',
    Post: 'ClinicalSummary.Post',
  },

  MedicalConditions: {
    Get: 'MedicalConditions.Get',
    Put: 'MedicalConditions.Put',
    Autopopulate: {
      Get: 'MedicalConditions.Autopopulate.Get',
    },
    DiagnosisRange: {
      Get: 'MedicalConditions.DiagnosisRange.Get',
    },
    DiagnosisCode: {
      Get: 'MedicalConditions.DiagnosisCode.Get',
    },
  },

  Cancer: {
    Get: 'Cancer.Get',
    Put: 'Cancer.Put',
    IsNeoplasms: {
      Get: 'Cancer.IsNeoplasms.Get',
    },
  },

  LabResults: {
    CompleteBloodCount: {
      Get: 'LabResults.CompleteBloodCount.Get',
      Put: 'LabResults.CompleteBloodCount.Put',
    },
    ChemistryPanelTest: {
      Get: 'LabResults.ChemistryPanelTest.Get',
      Put: 'LabResults.ChemistryPanelTest.Put',
    },
    TumorMarkerTest: {
      Get: 'LabResults.TumorMarkerTest.Get',
      Put: 'LabResults.TumorMarkerTest.Put',
    },
    OtherTestResult: {
      Get: 'LabResults.OtherTestResult.Get',
      Put: 'LabResults.OtherTestResult.Put',
    },
    LiverFunctionTest: {
      Get: 'LabResults.LiverFunctionTest.Get',
      Put: 'LabResults.LiverFunctionTest.Put',
    },
    KidneyFunctionTest: {
      Get: 'LabResults.KidneyFunctionTest.Get',
      Put: 'LabResults.KidneyFunctionTest.Put',
    },
    PatientLabTest: {
      Get: 'LabResults.PatientLabTest.Get',
    },
  },

  GeneticVariantFound: {
    Get: 'GeneticVariantFound.Get',
    Put: 'GeneticVariantFound.Put',
  },

  GenomicFindings: {
    Get: 'GenomicFindings.Get',
    Put: 'GenomicFindings.Put',
  },

  BloodPressureHistology: {
    Get: 'BloodPressureHistology.Get',
    Put: 'BloodPressureHistology.Put',
  },

  Eligibility: {
    Get: 'Eligibility.Get',
    Put: 'Eligibility.Put',
  },

  AdditionalPatients: {
    Get: 'AdditionalPatients.Get',
    Put: 'AdditionalPatients.Put',
  },

  CompetentAuthority: {
    Get: 'CompetentAuthority.Get',
    Put: 'CompetentAuthority.Put',
    Autopopulate: {
      Get: 'CompetentAuthority.Autopopulate.Get',
    },
  },

  PatientCoordinator: {
    Get: 'PatientCoordinator.Get',
    Put: 'PatientCoordinator.Put',
    Autopopulate: {
      Get: 'PatientCoordinator.Autopopulate.Get',
    },
  },

  Submission: {
    Get: 'Submission.Get',
    Put: 'Submission.Put',
  },

  SuccessCreateApplication: {
    Get: 'SuccessCreateApplication.Get',
  },

  SuitablePatients: {
    Get: 'SuitablePatients.Get',
  },

  PatientMatchingData: {
    Get: 'PatientMatchingData.Get',
  },
  PatientSelected: {
    Put: 'PatientSelected.Put',
  },

  IrbIec: {
    Get: 'IrbIec.Get',
    Put: 'IrbIec.Put',
    Autopopulate: {
      Get: 'IrbIec.Autopopulate.Get',
    },
  },

  IndependentIrb: {
    Get: 'IndependentIrb.Get',
    Put: 'IndependentIrb.Put',
    Autopopulate: {
      Get: 'IndependentIrb.Autopopulate.Get',
    },
  },

  DrugName: {
    Get: 'DrugName.Get',
  },
  DrugForm: {
    Get: 'DrugForm.Get',
  },

  DiagnosisCode: {
    Search: {
      Get: 'DiagnosisCode.Search.Get',
    },
    Range: {
      Get: 'DiagnosisCode.Range.Get',
    },
  },

  Documents: {
    Get: 'Documents.Get',
    Put: 'Documents.Put',
  },

  SponsorDocuments: {
    Get: 'SponsorDocuments.Get',
    Put: 'SponsorDocuments.Put',
  },
} as const;

// Helper to extract all endpoint string values from nested structure
type ExtractEndpointValues<T> = T extends string
  ? T
  : T extends object
  ? {
      [K in keyof T]: ExtractEndpointValues<T[K]>;
    }[keyof T]
  : never;

// Union type for all semantic endpoint string values
export type ApplicationEndpointValue = ExtractEndpointValues<
  typeof ApplicationEndpoints
>;

/**
 * HTTP Methods
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

/**
 * Endpoint definition for a specific API version
 */
interface EndpointDefinition {
  method: HttpMethod;
  path: string; // Template string with placeholders like {applicationId}, {id}
  queryParams?: Record<string, string | number>; // Static query params if needed
}

/**
 * Matrix mapping API versions (1, 2, 3) to endpoint definitions or null
 */
type ApiVersionMatrix = Record<1 | 2 | 3, EndpointDefinition | null>;

/**
 * Complete endpoint matrix mapping semantic endpoints to version matrices
 */
type EndpointMatrix = Record<ApplicationEndpointValue, ApiVersionMatrix>;

/**
 * Result object returned by getApplicationEndpoint
 */
export interface GetEndpointResult {
  url: string; // Fully qualified URL ready for API client
  method: HttpMethod;
  apiVersion: 1 | 2 | 3; // The API version that was selected
}

/**
 * Complete endpoint matrix with all applications endpoints
 */
const APPLICATION_ENDPOINT_MATRIX: EndpointMatrix = {
  'Version.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/version',
    },
    3: null,
  },

  'Type.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/type',
    },
    3: null,
  },

  'Sidebar.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/sidebar',
    },
    2: null,
    3: null,
  },

  'Status.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/status',
    },
    2: null,
    3: null,
  },

  'Status.RequestForAdditionalInfo.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/request-for-additional-info',
    },
    2: null,
    3: null,
  },

  'Status.RequestForAdditionalInfo.Put': {
    1: {
      method: 'PUT',
      path: '/applications/requested-for-additional-info',
    },
    2: null,
    3: null,
  },

  'Status.Resubmit.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/re-submit',
    },
    2: null,
    3: null,
  },

  'Status.Resubmit.Put': {
    1: {
      method: 'PUT',
      path: '/applications/resubmit',
    },
    2: null,
    3: null,
  },

  'Status.Decline.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/decline',
    },
    2: null,
    3: null,
  },

  'Status.Decline.Put': {
    1: {
      method: 'PUT',
      path: '/applications/declined',
    },
    2: null,
    3: null,
  },

  'Status.Withdraw.Put': {
    1: {
      method: 'PUT',
      path: '/applications/{applicationId}/withdrawn',
    },
    2: null,
    3: null,
  },

  'Status.Approve.Put': {
    1: {
      method: 'PUT',
      path: '/applications/{applicationId}/approved',
    },
    2: {
      method: 'PUT',
      path: '/applications/approved',
    },
    3: null,
  },

  'List.ByResearchSite.Get': {
    1: {
      method: 'GET',
      path: '/applications/my',
    },
    2: null,
    3: null,
  },

  'List.BySponsor.Get': {
    1: {
      method: 'GET',
      path: '/applications/{sponsorId}',
    },
    2: null,
    3: null,
  },

  'List.ByPatient.Get': {
    1: {
      method: 'GET',
      path: '/applications/patient/{patientId}',
    },
    2: null,
    3: null,
  },

  'List.Draft.Search.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/my/search-draft',
    },
    3: null,
  },

  'List.FilterData.Get': {
    1: {
      method: 'GET',
      path: '/applications/filter-data',
    },
    2: null,
    3: null,
  },

  'List.FilterDataForPatient.Get': {
    1: {
      method: 'GET',
      path: '/applications/patient/{patientId}/filter-data',
    },
    2: null,
    3: null,
  },

  'Create.Post': {
    1: {
      method: 'POST',
      path: '/applications',
    },
    2: null,
    3: null,
  },

  'History.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/history',
    },
    2: null,
    3: null,
  },

  'StudyIdentification.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/study-identification',
    },
    2: null,
    3: null,
  },

  'StudyIdentification.Put': {
    1: {
      method: 'PUT',
      path: '/applications/study-identification',
    },
    2: null,
    3: null,
  },

  'StudyIdentification.AutopopulateCountry.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/autopopulate-country-for-study-identification',
    },
    2: null,
    3: null,
  },

  'ResearchSite.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/research-site',
    },
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/research-site',
    },
    3: null,
  },

  'ResearchSite.Put': {
    1: {
      method: 'PUT',
      path: '/applications/research-site',
    },
    2: {
      method: 'PUT',
      path: '/applications/research-site',
    },
    3: null,
  },

  'ResearchSite.Autopopulate.Get': {
    1: {
      method: 'GET',
      path: '/applications/autopopulate-research-site',
    },
    2: null,
    3: null,
  },

  'ClinicalLaboratoryFacility.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/clinical-laboratory-facility',
    },
    2: null,
    3: null,
  },

  'ClinicalLaboratoryFacility.Put': {
    1: {
      method: 'PUT',
      path: '/applications/clinical-laboratory-facility',
    },
    2: null,
    3: null,
  },

  'ResearchSiteAndClinicalLab.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/research-site-and-clinical-laboratory-facility',
    },
    2: null,
    3: null,
  },

  'ResearchSiteAndClinicalLab.Put': {
    1: {
      method: 'PUT',
      path: '/applications/research-site-and-clinical-laboratory-facility',
    },
    2: null,
    3: null,
  },

  'ResearchSiteAndClinicalLab.Autopopulate.Get': {
    1: {
      method: 'GET',
      path: '/applications/autopopulate-research-site-and-clinical-laboratory-facility',
    },
    2: {
      method: 'GET',
      path: '/applications/autopopulate-research-site-and-clinical-laboratory-facility',
    },
    3: null,
  },

  'EligiblePersonnel.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/eligible-personnel-responsible',
    },
    2: null,
    3: null,
  },

  'EligiblePersonnel.Put': {
    1: {
      method: 'PUT',
      path: '/applications/eligible-personnel-responsible',
    },
    2: null,
    3: null,
  },

  'EligiblePersonnel.Autopopulate.Get': {
    1: {
      method: 'GET',
      path: '/applications/autopopulate-eligible-personnel-responsible',
    },
    2: {
      method: 'GET',
      path: '/applications/autopopulate-eligible-personnel-responsible',
    },
    3: null,
  },

  'ContactPoint.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/contact-point',
    },
    2: null,
    3: null,
  },

  'ContactPoint.Put': {
    1: {
      method: 'PUT',
      path: '/applications/contact-point',
    },
    2: null,
    3: null,
  },

  'ContactPoint.Autopopulate.Get': {
    1: {
      method: 'GET',
      path: '/applications/autopopulate-contact-point',
    },
    2: null,
    3: null,
  },

  'AdditionalContactPoint.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/additional-contact-point',
    },
    3: null,
  },

  'AdditionalContactPoint.Put': {
    1: null,
    2: {
      method: 'PUT',
      path: '/applications/additional-contact-point',
    },
    3: null,
  },

  'InstitutionOfficial.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/institution-official-at-the-research-site',
    },
    2: null,
    3: null,
  },

  'InstitutionOfficial.Put': {
    1: {
      method: 'PUT',
      path: '/applications/institution-official-at-the-research-site',
    },
    2: null,
    3: null,
  },

  'InstitutionOfficial.Autopopulate.Get': {
    1: {
      method: 'GET',
      path: '/applications/autopopulate-institution-official-at-the-research-site',
    },
    2: null,
    3: null,
  },

  'PatientDemographics.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/general-information',
    },
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/general-information',
    },
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/patient-demographics',
    },
  },

  'PatientDemographics.Put': {
    1: {
      method: 'PUT',
      path: '/applications/general-information',
    },
    2: {
      method: 'PUT',
      path: '/applications/general-information',
    },
    3: null, // V3 uses POST instead
  },

  'PatientDemographics.Post': {
    1: null,
    2: null,
    3: {
      method: 'POST',
      path: '/applications/{applicationId}/patient-demographics',
    },
  },

  'ClinicalSummary.Get': {
    1: null,
    2: null,
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/patient-clinical-summary',
    },
  },

  'ClinicalSummary.Post': {
    1: null,
    2: null,
    3: {
      method: 'POST',
      path: '/applications/{applicationId}/patient-clinical-summary',
    },
  },

  'MedicalConditions.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/information-related-current-medical-conditions',
    },
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/information-related-current-medical-conditions',
    },
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/patient-medical-conditions',
    },
  },

  'MedicalConditions.Put': {
    1: {
      method: 'PUT',
      path: '/applications/information-related-current-medical-conditions',
    },
    2: {
      method: 'PUT',
      path: '/applications/information-related-current-medical-conditions',
    },
    3: {
      method: 'PUT',
      path: '/applications/patient-medical-conditions',
    },
  },

  'MedicalConditions.Autopopulate.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/autopopulate/information-related-current-medical-conditions',
      queryParams: { PageSize: 0 },
    },
    3: null,
  },

  'MedicalConditions.DiagnosisRange.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{diagnosisChapterId}/diagnosis-range',
      queryParams: { PageSize: 0 },
    },
    3: null,
  },

  'MedicalConditions.DiagnosisCode.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/diagnosis-code',
      queryParams: { PageSize: 0 },
    },
    3: null,
  },

  // Cancer
  'Cancer.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/cancer',
    },
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/cancer',
    },
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/cancer',
    },
  },

  'Cancer.Put': {
    1: {
      method: 'PUT',
      path: '/applications/cancer',
    },
    2: {
      method: 'PUT',
      path: '/applications/cancer',
    },
    3: {
      method: 'PUT',
      path: '/applications/cancer',
    },
  },

  'Cancer.IsNeoplasms.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/is-neoplasms',
    },
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/is-neoplasms',
    },
  },

  'LabResults.CompleteBloodCount.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/complete-blood-count',
    },
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/complete-blood-count',
    },
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/complete-blood-count',
    },
  },

  'LabResults.CompleteBloodCount.Put': {
    1: {
      method: 'PUT',
      path: '/applications/complete-blood-count',
    },
    2: {
      method: 'PUT',
      path: '/applications/complete-blood-count',
    },
    3: {
      method: 'PUT',
      path: '/applications/{applicationId}/complete-blood-count',
    },
  },

  'LabResults.ChemistryPanelTest.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/chemistry-panel-test',
    },
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/chemistry-panel-test',
    },
  },

  'LabResults.ChemistryPanelTest.Put': {
    1: null,
    2: {
      method: 'PUT',
      path: '/applications/chemistry-panel-test',
    },
    3: {
      method: 'PUT',
      path: '/applications/{applicationId}/chemistry-panel-test',
    },
  },

  'LabResults.TumorMarkerTest.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/tumor-marker-test',
    },
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/tumor-marker-test',
    },
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/tumor-marker-test',
    },
  },

  'LabResults.TumorMarkerTest.Put': {
    1: {
      method: 'PUT',
      path: '/applications/tumor-marker-test',
    },
    2: {
      method: 'PUT',
      path: '/applications/tumor-marker-test',
    },
    3: {
      method: 'PUT',
      path: '/applications/{applicationId}/tumor-marker-test',
    },
  },

  'LabResults.OtherTestResult.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/other-test-result',
    },
    2: null, // V2 doesn't have GET for other-test-result (only PUT)
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/other-test-result',
    },
  },

  'LabResults.OtherTestResult.Put': {
    1: {
      method: 'PUT',
      path: '/applications/other-test-result',
    },
    2: null, // Note: V2 PUT exists but path might be different - checking...
    3: {
      method: 'PUT',
      path: '/applications/{applicationId}/other-test-result',
    },
  },

  'LabResults.LiverFunctionTest.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/liver-function-test',
    },
    2: null,
    3: null,
  },

  'LabResults.LiverFunctionTest.Put': {
    1: {
      method: 'PUT',
      path: '/applications/liver-function-test',
    },
    2: null,
    3: null,
  },

  'LabResults.KidneyFunctionTest.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/kidney-function-test',
    },
    2: null,
    3: null,
  },

  'LabResults.KidneyFunctionTest.Put': {
    1: {
      method: 'PUT',
      path: '/applications/kidney-function-test',
    },
    2: null,
    3: null,
  },

  'LabResults.PatientLabTest.Get': {
    1: null,
    2: null,
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/patient-lab-test',
    },
  },

  'GeneticVariantFound.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/genetic-variant-found',
    },
    2: null,
    3: null,
  },

  'GeneticVariantFound.Put': {
    1: {
      method: 'PUT',
      path: '/applications/genetic-variant-found',
    },
    2: null,
    3: null,
  },

  'GenomicFindings.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/genomic-findings-pertinent-to-this-request',
    },
    3: null,
  },

  'GenomicFindings.Put': {
    1: null,
    2: {
      method: 'PUT',
      path: '/applications/genomic-findings-pertinent-to-this-request',
    },
    3: null,
  },

  'BloodPressureHistology.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/blood-presure-and-histology',
    },
    2: null,
    3: null,
  },

  'BloodPressureHistology.Put': {
    1: {
      method: 'PUT',
      path: '/applications/blood-presure-and-histology',
    },
    2: null,
    3: null,
  },

  'Eligibility.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/eligibility',
    },
    2: null,
    3: null,
  },

  'Eligibility.Put': {
    1: {
      method: 'PUT',
      path: '/applications/eligibility',
    },
    2: null,
    3: null,
  },

  'AdditionalPatients.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/additional-patients',
    },
    2: null,
    3: null,
  },

  'AdditionalPatients.Put': {
    1: {
      method: 'PUT',
      path: '/applications/additional-patients',
    },
    2: null,
    3: null,
  },

  'CompetentAuthority.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/competent-authority-ethics-committee',
    },
    2: null,
    3: null,
  },

  'CompetentAuthority.Put': {
    1: {
      method: 'PUT',
      path: '/applications/competent-authority-ethics-committee',
    },
    2: null,
    3: null,
  },

  'CompetentAuthority.Autopopulate.Get': {
    1: {
      method: 'GET',
      path: '/applications/autopopulate-competent-authority-ethics-committee',
    },
    2: null,
    3: null,
  },

  'PatientCoordinator.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/patient-coordinator-and-primary-contact',
    },
    2: null,
    3: null,
  },

  'PatientCoordinator.Put': {
    1: {
      method: 'PUT',
      path: '/applications/patient-coordinator',
    },
    2: null,
    3: null,
  },

  'PatientCoordinator.Autopopulate.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/autopopulate-study-coordinator',
    },
    2: null,
    3: null,
  },

  'Submission.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/submission-of-the-application',
    },
    2: null,
    3: null,
  },

  'Submission.Put': {
    1: {
      method: 'PUT',
      path: '/applications/submission-of-the-application',
    },
    2: null,
    3: null,
  },

  'SuccessCreateApplication.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/success-create-application',
    },
    2: null,
    3: null,
  },

  'SuitablePatients.Get': {
    1: {
      method: 'GET',
      path: '/applications/{applicationId}/suitable-patients',
    },
    2: null,
    3: null,
  },

  'PatientMatchingData.Get': {
    1: null,
    2: null,
    3: {
      method: 'GET',
      path: '/applications/{applicationId}/patient-matching-data',
    },
  },

  'PatientSelected.Put': {
    1: null,
    2: null,
    3: {
      method: 'PUT',
      path: '/applications/{applicationId}/selected-patient',
    },
  },

  'IrbIec.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/irb-iec',
    },
    3: null,
  },

  'IrbIec.Put': {
    1: null,
    2: {
      method: 'PUT',
      path: '/applications/irb-iec',
    },
    3: null,
  },

  'IrbIec.Autopopulate.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/autopopulate-irb-iec',
    },
    3: null,
  },

  'IndependentIrb.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/independent-irb',
    },
    3: null,
  },

  'IndependentIrb.Put': {
    1: null,
    2: {
      method: 'PUT',
      path: '/applications/independent-irb',
    },
    3: null,
  },

  'IndependentIrb.Autopopulate.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/autopopulate-independent-irb',
    },
    3: null,
  },

  'Documents.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/documents',
    },
    3: null,
  },

  'Documents.Put': {
    1: null,
    2: {
      method: 'PUT',
      path: '/applications/documents',
    },
    3: null,
  },

  'SponsorDocuments.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{applicationId}/sponsor-documents',
    },
    3: null,
  },

  'SponsorDocuments.Put': {
    1: null,
    2: {
      method: 'PUT',
      path: '/applications/sponsor-documents',
    },
    3: null,
  },

  'DrugName.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/drug-name',
    },
    3: null,
  },

  'DrugForm.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/form',
    },
    3: null,
  },

  'DiagnosisCode.Search.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/diagnosis-code',
    },
    3: null,
  },

  'DiagnosisCode.Range.Get': {
    1: null,
    2: {
      method: 'GET',
      path: '/applications/{diagnosisChapterId}/diagnosis-range',
      queryParams: { PageSize: 0 },
    },
    3: null,
  },
} as const satisfies EndpointMatrix;

/**
 * Get application endpoint URL and method based on semantic endpoint and application version.
 * Automatically handles version fallback (only downward: V3 → V2 → V1).
 *
 * @param semanticEndpoint - The semantic endpoint key from ApplicationEndpoints
 * @param applicationVersion - The application entity version (1, 2, or 3)
 * @param pathParams - Path parameters to replace in the URL template (e.g., { applicationId: '123', id: '123' })
 * @param additionalQueryParams - Additional query parameters beyond api-version
 * @returns Object with url, method, and apiVersion
 * @throws Error if endpoint not found in matrix or no version available
 */
export function getApplicationEndpoint<T extends ApplicationEndpointValue>(
  semanticEndpoint: T,
  applicationVersion: 1 | 2 | 3,
  pathParams: Record<string, string | number> = {},
  additionalQueryParams: Record<
    string,
    string | number | ReadonlyArray<string> | ReadonlyArray<number>
  > = {},
): GetEndpointResult {
  // 1. Get version matrix for this semantic endpoint
  const versionMatrix = APPLICATION_ENDPOINT_MATRIX[semanticEndpoint];

  if (!versionMatrix) {
    throw new Error(
      `Application endpoint "${semanticEndpoint}" not found in matrix`,
    );
  }

  // 2. Start from requested application version, fallback downward
  let targetVersion = applicationVersion;
  let selectedDefinition: EndpointDefinition | null = null;

  // 3. Find first existing endpoint starting from targetVersion, falling back
  for (let version = targetVersion; version >= 1; version--) {
    const def = versionMatrix[version as keyof ApiVersionMatrix];
    if (def !== null) {
      selectedDefinition = def;
      targetVersion = version;
      break;
    }
  }

  if (!selectedDefinition) {
    throw new Error(
      `No endpoint found for "${semanticEndpoint}" in API version ${applicationVersion} or below`,
    );
  }

  // 4. Replace path parameters in template
  // Caller is responsible for providing correct parameter names
  let url = selectedDefinition.path;
  for (const [key, value] of Object.entries(pathParams)) {
    url = url.replace(`{${key}}`, String(value));
  }

  // 6. Build query string
  const queryParams = {
    'api-version': targetVersion,
    ...(selectedDefinition.queryParams || {}),
    ...additionalQueryParams,
  };

  const queryString = Object.entries(queryParams)
    .filter(([_, value]) => value !== undefined && value !== null)
    .flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map(
          (item) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`,
        );
      }
      return [
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
      ];
    })
    .join('&');

  url = `${url}?${queryString}`;

  return {
    url,
    method: selectedDefinition.method,
    apiVersion: targetVersion,
  };
}
